import { PlayArrow, Refresh } from '@mui/icons-material'; // Import Refresh icon
import { Box, Drawer, Grid, IconButton, Tooltip, Typography } from '@mui/material';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { TextureDrawerProps } from '../../../@types/ContextProviderRelated';
import { EElementDirection, EProgressType, EProgressVariant, ESubMenu } from '../../../@types/Enums';
import { EAssetType, IFilesApiColor, IFilesApiMaterial, TTexture } from '../../../@types/FilesAPIModels';
import { useBuilding } from '../../../provider/BuildingProvider/BuildingProvider';
import { useRoomComponentsData } from '../../../provider/RoomComponentsDataProvider';
import { useToolbarContext } from '../../../provider/ToolbarProvider';
import { immersightBlue } from '../../../utils/colors';
import ImsProgress from '../ImsProgress';
import ImsTabs from '../ImsTabs';
import TextureSearchPanel from '../TextureSearchPanel';
import TexturesGallery from '../TexturesGallery';

const TexturesDrawer = (props: TextureDrawerProps): JSX.Element => {
  const { id, open, onClose } = props;
  const { setCurrentlyActiveSubMenu } = useToolbarContext();
  const { setSelectedModel: setSelectedObject } = useBuilding();

  // Pull data from provider
  const {
    textureProducts,
    textures,
    textureLoadProgress,
    textureLoadTarget,
    textureFetchCompleted,
    isUserSearching,
    ensureTextures,
    refetchTextures,
    isFetchingTextures,
    isFetchingProductsAndModels,
  } = useRoomComponentsData();
  const hasRequestedInitialLoadRef = useRef(false);

  useEffect(() => {
    if (!open || hasRequestedInitialLoadRef.current) return;

    hasRequestedInitialLoadRef.current = true;
    void ensureTextures();
  }, [open, ensureTextures]);

  useEffect(
    () => () => {
      // on unmount
      setSelectedObject(null);
    },
    [setSelectedObject],
  );

  // Calculate effective progress values
  const effectiveTextureTarget = textureLoadTarget || 0;
  const effectiveTextureProgress = textureLoadProgress || 0;

  // Local states for tab selection, splitting textures, and loading
  const [selectedTab, setSelectedTab] = useState<number>(0);
  const [materials, setMaterials] = useState<IFilesApiMaterial[]>([]);
  const [colors, setColors] = useState<IFilesApiColor[]>([]);
  const [isDrawerLoading, setIsDrawerLoading] = useState<boolean>(!textureFetchCompleted);
  const [searchedResults, setSearchedResults] = useState<any[]>([]);

  // Update loading state based on fetch progress and completion
  useEffect(() => {
    if (!textureFetchCompleted || (effectiveTextureTarget > 0 && effectiveTextureProgress < effectiveTextureTarget)) {
      setIsDrawerLoading(true);
    } else {
      setIsDrawerLoading(false);
    }
  }, [textureFetchCompleted, effectiveTextureProgress, effectiveTextureTarget]);

  // Split textures into materials and colors
  useEffect(() => {
    if (textureFetchCompleted && textures?.length > 0) {
      const tmpMaterials = textures.filter((t: TTexture) => t.assetType === EAssetType.MATERIAL) as IFilesApiMaterial[];
      const tmpColors = textures.filter((t: TTexture) => t.assetType === EAssetType.COLOR) as IFilesApiColor[];
      setMaterials(tmpMaterials);
      setColors(tmpColors);
    }
  }, [textures, textureFetchCompleted]);

  // Close Drawer Handler
  const handleCloseDrawer = () => {
    setIsDrawerLoading(false);
    setCurrentlyActiveSubMenu(ESubMenu.NONE);
    setSelectedObject(null);
    onClose();
  };

  // Decide which items to pass to the search panel based on the selected tab
  const itemsForSearch = useMemo(() => {
    if (selectedTab === 0) {
      return textureProducts || [];
    } else if (selectedTab === 1) {
      return materials;
    }
    return colors;
  }, [selectedTab, textureProducts, materials, colors]);

  // When search results change, they override the default items
  const productsToShow = selectedTab === 0 ? (isUserSearching ? searchedResults : textureProducts) : [];
  const materialsToShow = selectedTab === 1 ? (isUserSearching ? searchedResults : materials) : [];
  const colorsToShow = selectedTab === 2 ? (isUserSearching ? searchedResults : colors) : [];

  return (
    <Drawer
      key={id}
      anchor="right"
      open={open}
      variant={open ? 'permanent' : 'temporary'}
      sx={{
        '& .MuiPaper-root': {
          top: `64px`,
          zIndex: 1000,
          width: `340px`,
          height: `calc(100% - 64px)`,
          overflow: 'hidden',
        },
      }}
    >
      <Grid container direction="column" spacing={0.5} sx={{ width: '96.5%', margin: '0' }}>
        {/* Drawer Header */}
        <Grid
          item
          sx={{
            backgroundColor: immersightBlue,
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: 0.5,
          }}
        >
          <Box sx={{ marginLeft: '16px' }}>
            <Typography variant="h6" sx={{ textTransform: 'uppercase' }}>
              <FormattedMessage id="TextureDrawer.header" defaultMessage="Surfaces" />
            </Typography>
          </Box>
          <IconButton onClick={handleCloseDrawer} sx={{ color: '#fff', '&:hover': { backgroundColor: 'transparent' } }}>
            <PlayArrow sx={{ fontSize: 30, marginRight: 0.3 }} />
          </IconButton>
        </Grid>

        {/* Tabs Header */}
        <Grid item container spacing={1.5} sx={{ marginLeft: 0.25, marginTop: 1, padding: 0 }}>
          {/* Tabs: Products / Materials / Colors */}
          <Grid item xs={11.5}>
            <ImsTabs
              tabs={[{ id: 'TextureDrawer.products' }, { id: 'TextureDrawer.materials' }, { id: 'TextureDrawer.color' }]}
              initialSelectedIndex={selectedTab}
              onTabChange={(index) => {
                setSelectedTab(index);
                setSearchedResults([]);
              }}
            />
          </Grid>

          {/* Circular loading using effective progress values */}
          <Grid container justifyContent="center" alignItems="center">
            {isDrawerLoading && effectiveTextureTarget !== 0 && (
              <ImsProgress
                variant={EProgressVariant.determinate}
                direction={EElementDirection.LEFT_TO_RIGHT}
                type={EProgressType.circular}
                value={effectiveTextureProgress > 0 ? effectiveTextureProgress : 0}
                min={0}
                max={effectiveTextureTarget}
                showLabel={true}
                labelUnit="%"
                progressColor={immersightBlue}
                backgroundColor="transparent !important"
                style={{ pointerEvents: 'none' }}
              />
            )}
            {/* Show refresh button if not fetching textures and not fetching products/models */}
            {!isFetchingTextures && !isFetchingProductsAndModels && (
              <Tooltip title={<FormattedMessage id="RoomComponentsDrawer.reloadTooltip" defaultMessage="Reload" />} arrow>
                <IconButton
                  onClick={refetchTextures}
                  sx={{
                    p: 0.5,
                    backgroundColor: immersightBlue,
                    color: '#fff',
                    borderRadius: '50%',
                    '&:hover': { backgroundColor: immersightBlue },
                  }}
                >
                  <Refresh />
                </IconButton>
              </Tooltip>
            )}
          </Grid>
        </Grid>

        {/* Search Panel */}
        <TextureSearchPanel selectedTab={selectedTab} items={itemsForSearch} returnResults={(results) => setSearchedResults(results)} />

        {/* Main content: the gallery of items */}
        <TexturesGallery selectedTab={selectedTab} products={productsToShow} materials={materialsToShow} colors={colorsToShow} />
      </Grid>
    </Drawer>
  );
};

export default TexturesDrawer;
